import { t } from '@lingui/core/macro'

export interface PaymentMethodExtraField {
    label: string
    type: string,
    name: string
    validation: (value: string) => string | null
}

export const getPaymentMethodExtraField = (name: string): PaymentMethodExtraField | undefined => {
    const paymentMethodExtraFields: Record<string, PaymentMethodExtraField> = {
        phone: {
            label: t`Телефон`,
            type: 'string',
            name: 'phone',
            validation: (phone: string): string | null => {
                const cleanPhone = phone.trim()

                if (cleanPhone.length === 0) {
                    return t`Номер телефона не должен быть пустым`
                }

                if (cleanPhone.length < 6 || cleanPhone.length > 18) {
                    return t`Номер телефона должен содержать от 6 до 18 цифр`
                }

                if (!/\d/.test(cleanPhone)) {
                    return t`Номер телефона должен содержать только цифры`
                }

                return null
            },
        },
        country: {
            label: t`Страна`,
            type: 'country',
            name: 'country',
            validation: (country: string): string | null => {
                const cleanCountry = country.trim()

                if (cleanCountry.length === 0) {
                    return t`Код страны не должен быть пустым`
                }

                if (cleanCountry.length !== 2) {
                    return t`Код страны должен содержать 2 символа`
                }

                if (!/[A-Z]/.test(cleanCountry)) {
                    return t`Код страны должен содержать только буквы латинского алфавита`
                }

                return null
            },
        },
        city: {
            label: t`Город`,
            type: 'string',
            name: 'city',
            validation: (city: string): string | null => {
                const cleanCity = city.trim()

                if (cleanCity.length === 0) {
                    return t`Название города не должно быть пустым`
                }

                if (cleanCity.length < 2 || cleanCity.length > 50) {
                    return t`Название города должно содержать от 2 до 50 символов`
                }

                if (!/^[a-zA-Z\s'-]+$/.test(cleanCity)) {
                    return t`Название города должно состоять из букв латинского алфавита`
                }

                return null
            },
        },
        district: {
            label: t`Район`,
            type: 'string',
            name: 'district',
            validation: (district: string): string | null => {
                const cleanDistrict = district.trim()

                if (cleanDistrict.length === 0) {
                    return t`Название района не должно быть пустым`
                }

                if (cleanDistrict.length < 2 || cleanDistrict.length > 50) {
                    return t`Название района должно содержать от 2 до 50 символов`
                }

                if (!/\w+/.test(cleanDistrict)) {
                    return t`Название района должно содержать буквы или цифры`
                }

                return null
            },
        },
        street: {
            label: t`Улица`,
            type: 'string',
            name: 'street',
            validation: (street: string): string | null => {
                const cleanStreet = street.trim()

                if (cleanStreet.length === 0) {
                    return t`Название улицы не должно быть пустым`
                }

                if (cleanStreet.length < 2 || cleanStreet.length > 50) {
                    return t`Название улицы должно содержать от 2 до 50 символов`
                }

                if (!/\w+/.test(cleanStreet)) {
                    return t`Название улицы должно содержать буквы или цифры`
                }

                return null
            },
        },
        building: {
            label: t`Дом`,
            type: 'string',
            name: 'building',
            validation: (building: string): string | null => {
                const cleanBuilding = building.trim()

                if (cleanBuilding.length === 0) {
                    return t`Номер дома не должен быть пустым`
                }

                if (cleanBuilding.length < 2 || cleanBuilding.length > 50) {
                    return t`Номер дома должен содержать от 2 до 50 символов`
                }

                if (!/\w+/.test(cleanBuilding)) {
                    return t`Номер дома должен содержать буквы или цифры`
                }

                return null
            },
        },
        zipcode: {
            label: t`Почтовый индекс`,
            type: 'string',
            name: 'zipcode',
            validation: (zipcode: string): string | null => {
                const cleanZipcode = zipcode.trim()

                if (cleanZipcode.length === 0) {
                    return t`Почтовый индекс не должен быть пустым`
                }

                if (cleanZipcode.length < 3 || cleanZipcode.length > 30) {
                    return t`Почтовый индекс должен содержать от 3 до 30 символов`
                }

                if (!/^[A-Z0-9 ]+$/.test(cleanZipcode)) {
                    return t`Почтовый индекс указан в некорректном формате`
                }

                return null
            },
        },
        birth: {
            label: t`Дата рождения`,
            type: 'date',
            name: 'birth',
            validation: (birth: string): string | null => {
                const cleanBirth = birth.trim()

                if (cleanBirth.length === 0) {
                    return t`Дата рождения не должна быть пустой`
                }

                if (!/^\d{4}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12][0-9]|3[01])$/.test(cleanBirth)) {
                    return t`Дата рождения должна быть в формате ГГГГ-ММ-ДД`
                }

                return null
            },
        },
        firstName: {
            label: t`Имя`,
            type: 'string',
            name: 'firstName',
            validation: (firstName: string): string | null => {
                const cleanFirstName = firstName.trim()

                if (cleanFirstName.length === 0) {
                    return t`Имя не должно быть пустым`
                }

                if (cleanFirstName.length < 2 || cleanFirstName.length > 30) {
                    return t`Имя должно содержать от 2 до 30 символов`
                }

                return null
            },
        },
        lastName: {
            label: t`Фамилия`,
            type: 'string',
            name: 'lastName',
            validation: (lastName: string): string | null => {
                const cleanLastName = lastName.trim()

                if (cleanLastName.length === 0) {
                    return t`Фамилия не должна быть пустой`
                }

                if (cleanLastName.length < 2 || cleanLastName.length > 30) {
                    return t`Фамилия должна содержать от 2 до 30 символов`
                }

                return null
            },
        },
        cpf: {
            label: 'CPF',
            type: 'string',
            name: 'cpf',
            validation: (cpf: string): string | null => {
                const cleanCpf = cpf.trim()

                if (cleanCpf.length === 0) {
                    return t`CPF не должен быть пустым`
                }

                if (cleanCpf.length !== 11) {
                    return t`CPF должен содержать ровно 11 цифр`
                }

                if (!/^\d+$/.test(cleanCpf)) {
                    return t`CPF должен содержать только цифры`
                }

                return null
            },
        },
    }

    return paymentMethodExtraFields[name] || undefined
}
